import axios, { AxiosInstance } from "axios";
import {
  IProduct,
  IPaginatedResponse,
  ICompatibilityResponse,
  IFilterSet,
  StoredBuildProduct,
  SaveBuildResponse,
  GetBuildResponse,
  IGlobalLinkSettings,
} from "../types";
import { CATEGORIES } from "../utils/constants";

interface TrackAnalyticsEvent {
  creatorSlug: string;
  sessionId: string;
  productId: string;
  productName: string;
  brandName: string;
  category: string;
  price: number;
  currency?: string;
  action: "added" | "removed";
}

interface BuildSnapshotEvent {
  creatorSlug: string;
  sessionId: string;
  totalPrice: number;
  currency?: string;
  components: Array<{
    productId: string;
    productName: string;
    brandName: string;
    category: string;
    price: number;
  }>;
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string = "";
  private creatorSlug: string = "";
  private sessionId: string = "";
  private globalLinkSettings: IGlobalLinkSettings | null = null;

  constructor() {
    this.api = axios.create({
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Generate a unique session ID for analytics tracking
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public configure(baseURL: string, creatorSlug?: string) {
    this.baseURL = baseURL;
    this.creatorSlug = creatorSlug || "";
    this.api.defaults.baseURL = baseURL;
  }

  public async trackAnalytics(
    event: Omit<TrackAnalyticsEvent, "creatorSlug" | "sessionId">,
  ): Promise<void> {
    try {
      if (!this.creatorSlug) {
        console.warn(
          "[ANALYTICS] No creator slug configured, skipping analytics tracking",
        );
        return;
      }

      await this.api.post("/api/v1/analytics/track", {
        ...event,
        creatorSlug: this.creatorSlug,
        sessionId: this.sessionId,
      });
    } catch (error) {
      console.error("Error tracking analytics:", error);
      // Don't throw error - analytics should not break the app
    }
  }

  public async trackBuildSnapshot(
    components: Array<{
      productId: string;
      productName: string;
      brandName: string;
      category: string;
      price: number;
    }>,
    totalPrice: number,
  ): Promise<void> {
    try {
      if (!this.creatorSlug) {
        console.warn(
          "[ANALYTICS] No creator slug configured, skipping build snapshot tracking",
        );
        return;
      }

      await this.api.post("/api/v1/analytics/track-build", {
        creatorSlug: this.creatorSlug,
        sessionId: this.sessionId,
        totalPrice,
        currency: "USD",
        components,
      });
    } catch (error) {
      console.error("Error tracking build snapshot:", error);
      // Don't throw error - analytics should not break the app
    }
  }

  public async trackCategoryBrowsing(category: string): Promise<void> {
    try {
      if (!this.creatorSlug) {
        console.warn(
          "[ANALYTICS] No creator slug configured, skipping category browsing tracking",
        );
        return;
      }

      await this.api.post("/api/v1/analytics/track-category", {
        creatorSlug: this.creatorSlug,
        sessionId: this.sessionId,
        category,
      });
    } catch (error) {
      console.error("Error tracking category browsing:", error);
      // Don't throw error - analytics should not break the app
    }
  }

  public async trackLinkClick(
    productId: string,
    productName: string,
    brandName: string,
    provider: string,
    region: string,
    isOverridden: boolean,
    hasProviderRule: boolean,
  ): Promise<void> {
    try {
      if (!this.creatorSlug) {
        console.warn(
          "[ANALYTICS] No creator slug configured, skipping link click tracking",
        );
        return;
      }

      await this.api.post("/api/v1/analytics/track-link-click", {
        creatorSlug: this.creatorSlug,
        sessionId: this.sessionId,
        productId,
        productName,
        brandName,
        provider,
        region,
        isOverridden,
        hasProviderRule,
      });
    } catch (error) {
      console.error("Error tracking link click:", error);
      // Don't throw error - analytics should not break the app
    }
  }

  public async getProducts(
    category: string,
    page: number = 1,
    search?: string,
    minPrice?: number,
    maxPrice?: number,
    filters?: Record<string, string>,
  ): Promise<IPaginatedResponse<IProduct>> {
    try {
      const params: any = {
        category,
        page,
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      if (minPrice !== undefined && maxPrice !== undefined) {
        params.price_range = `$${minPrice}+-+$${maxPrice}`;
      }

      if (filters) {
        Object.assign(params, filters);
      }

      if (this.creatorSlug) {
        params.creatorSlug = this.creatorSlug;
      }

      const response = await this.api.get<IPaginatedResponse<IProduct>>(
        "/api/v1/products/",
        { params },
      );

      // Store global link settings from response
      if (response.data.globalLinkSettings) {
        this.globalLinkSettings = response.data.globalLinkSettings;
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  }

  public getGlobalLinkSettings(): IGlobalLinkSettings | null {
    return this.globalLinkSettings;
  }

  /**
   * Get the rel attribute string for affiliate links
   * Combines noopener (for security) with any global settings
   */
  public getAffiliateLinkRel(): string {
    const defaultAttrs = ["noopener"];

    if (this.globalLinkSettings?.relAttributes?.length) {
      // Combine default attrs with global settings, avoiding duplicates
      const allAttrs = new Set([
        ...defaultAttrs,
        ...this.globalLinkSettings.relAttributes,
      ]);
      return Array.from(allAttrs).join(" ");
    }

    return defaultAttrs.join(" ");
  }

  public async getCompatibility(
    wheelbaseBrand: string,
    wheelBrand: string,
  ): Promise<ICompatibilityResponse> {
    try {
      // Call the external sim racing API directly for compatibility checks
      const externalApiUrl = "https://api.readysetsim.com";

      const response = await axios.get<ICompatibilityResponse>(
        `${externalApiUrl}/api/v1/products/checkcompatibility/`,
        {
          params: {
            wheelbasebrand: wheelbaseBrand,
            wheelbrand: wheelBrand,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching compatibility data:", error);
      throw error;
    }
  }

  public async getFilterItems(category: string): Promise<IFilterSet[]> {
    try {
      const externalApiUrl = "https://api.readysetsim.com";
      const response = await axios.get<IFilterSet[]>(
        `${externalApiUrl}/api/v1/products/filter_items/`,
        { params: { category } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching filter items:", error);
      throw error;
    }
  }

  public async getProductsByIds(
    productIds: Array<{ productId: number; categoryId: string }>,
  ): Promise<Record<string, IProduct>> {
    try {
      // Fetch products for each category in parallel
      const categoriesMap = new Map<string, number[]>();

      // Group product IDs by category
      for (const { productId, categoryId } of productIds) {
        const existing = categoriesMap.get(categoryId) || [];
        existing.push(productId);
        categoriesMap.set(categoryId, existing);
      }

      const results: Record<string, IProduct> = {};

      // Fetch products for each category
      await Promise.all(
        Array.from(categoriesMap.entries()).map(async ([categoryId, ids]) => {
          // Map categoryId to apiName for the API call
          const category = CATEGORIES.find((c) => c.id === categoryId);
          const apiName = category?.apiName || categoryId;

          // Fetch products from this category - search through pages to find the product
          let page = 1;
          let found = false;
          const targetIds = new Set(ids);

          while (!found && page <= 10) {
            const response = await this.getProducts(apiName, page);

            // Find matching products by ID
            for (const id of targetIds) {
              const product = response.results.find((p) => p.id === id);
              if (product) {
                results[categoryId] = product;
                targetIds.delete(id);
              }
            }

            // Stop if we found all products or no more pages
            if (targetIds.size === 0 || !response.next) {
              found = true;
            } else {
              page++;
            }
          }
        }),
      );

      return results;
    } catch (error) {
      console.error("Error fetching products by IDs:", error);
      throw error;
    }
  }

  public async saveBuild(
    products: StoredBuildProduct[],
    totalPrice: number,
  ): Promise<SaveBuildResponse> {
    try {
      const response = await this.api.post<SaveBuildResponse>(
        "/api/v1/builds/save",
        {
          products,
          creatorSlug: this.creatorSlug || undefined,
          totalPrice,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error saving build:", error);
      throw error;
    }
  }

  public async getBuildByCode(code: string): Promise<GetBuildResponse> {
    try {
      const response = await this.api.get<GetBuildResponse>(
        `/api/v1/builds/${code}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching build:", error);
      throw error;
    }
  }

  public getCreatorSlug(): string {
    return this.creatorSlug;
  }
}

export const apiService = new ApiService();
